import { useState, useMemo, useCallback, useEffect } from 'react'
import { isSameMonth, isSameWeek, parseISO } from 'date-fns'
import {
  getMonthGrid,
  getMonthTitle,
  getWeekGrid,
  nextMonth,
  prevMonth,
  nextWeek,
  prevWeek,
  type CalendarDay,
} from '@/lib/calendarUtils'
import { useUIStore } from '@/stores/uiStore'
import { formatWeekRange } from '@/lib/dateUtils'
import { formatDate } from '@/utils/format'

export function useCalendar() {
  const viewMode = useUIStore((s) => s.calendarViewMode)
  const selectedDate = useUIStore((s) => s.calendarSelectedDate)
  const setCalendarSelectedDate = useUIStore((s) => s.setCalendarSelectedDate)
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date())
  const [currentWeekBase, setCurrentWeekBase] = useState<Date>(new Date())

  // selectedDate(uiStore, MiniCalendar 등이 갱신)가 현재 표시 중인 월/주를 벗어나면
  // 월간 그리드·주간 베이스를 그 날짜로 이동시켜 동기화한다.
  // (deps는 selectedDate만 — currentMonth를 추가하면 수동 네비게이션이 다시 클램프됨)
  useEffect(() => {
    if (!selectedDate) return
    const d = parseISO(selectedDate)
    if (Number.isNaN(d.getTime())) return
    setCurrentMonth((prev) => (isSameMonth(d, prev) ? prev : d))
    setCurrentWeekBase((prev) => (isSameWeek(d, prev, { weekStartsOn: 0 }) ? prev : d))
  }, [selectedDate])

  const monthGrid: CalendarDay[] = useMemo(
    () => getMonthGrid(currentMonth),
    [currentMonth]
  )

  const weekGrid: CalendarDay[] = useMemo(
    () => getWeekGrid(currentWeekBase),
    [currentWeekBase]
  )

  const monthTitle = useMemo(
    () => getMonthTitle(currentMonth),
    [currentMonth]
  )

  const weekTitle = useMemo(
    () => formatWeekRange(currentWeekBase),
    [currentWeekBase]
  )

  // Month navigation
  const goToNextMonth = useCallback(() => {
    setCurrentMonth((prev) => nextMonth(prev))
  }, [])

  const goToPrevMonth = useCallback(() => {
    setCurrentMonth((prev) => prevMonth(prev))
  }, [])

  // Week navigation
  const goToNextWeek = useCallback(() => {
    setCurrentWeekBase((prev) => nextWeek(prev))
  }, [])

  const goToPrevWeek = useCallback(() => {
    setCurrentWeekBase((prev) => prevWeek(prev))
  }, [])

  const goToToday = useCallback(() => {
    const today = new Date()
    setCurrentMonth(today)
    setCurrentWeekBase(today)
    setCalendarSelectedDate(formatDate(today))
  }, [setCalendarSelectedDate])

  const selectDate = useCallback((dateStr: string) => {
    setCalendarSelectedDate(dateStr)
  }, [setCalendarSelectedDate])

  // Computed: today's row index in month grid (0-5)
  const todayRowIndex = useMemo(() => {
    const idx = monthGrid.findIndex((d) => d.isToday)
    return idx >= 0 ? Math.floor(idx / 7) : -1
  }, [monthGrid])

  return {
    viewMode,
    currentMonth,
    selectedDate,
    monthGrid,
    weekGrid,
    monthTitle,
    weekTitle,
    todayRowIndex,
    goToNextMonth,
    goToPrevMonth,
    goToNextWeek,
    goToPrevWeek,
    goToToday,
    selectDate,
  }
}
